import { useAction, useAtom, useCtx } from '@reatom/npm-react'
import { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Result } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { PremiumId, type PremiumMetaModel } from '@root/entities/premium'
import { PremiumCurrencyInfoAddress } from '@root/entities/premium/ui/premium-currency-info/premium-currency-info-address'
import { PremiumCurrencyInfoHeading } from '@root/entities/premium/ui/premium-currency-info/premium-currency-info-heading'
import { PremiumCurrencyMetricsTable } from '@root/entities/premium/ui/premium-currency-metrics-table/premium-currency-info-table'
import * as Socials from '@root/entities/socials/model/socials'
import {
  TelegramUrlFormControl,
  WebsiteUrlFormControl,
  XUrlFormControl,
} from '@root/shared/form-control'
import { mediaMinTablet, Separator } from '@root/shared/ui-kit'

import { reatomPremiumCurrencySocialLinksForm } from '../../atom/premium-currency-dialog'

import { PremiumCurrencyDetailsEmoji } from './premium-currency-details-emoji'
import { PremiumCurrencyScreenCurrencyImages } from './premium-currency-details-images'
import { PremiumCurrencyDetailsPremium } from './premium-currency-details-premium'

import type { CurrencyModel } from '@root/entities/currency'
import type { PairModel } from '@root/entities/pair'

type Props = {
  currency: CurrencyModel
  pair: PairModel | undefined
  premium: PremiumMetaModel
  onEditClick: () => void
  onBannerEditClick: () => void
  onEditEmoji: () => void
}

type LinksFormValues = {
  readonly websiteUrl: string
  readonly telegramUrl: string
  readonly xUrl: string
}

export const PremiumCurrencyDetailsContent: React.FC<Props> = ({
  currency,
  pair,
  premium,
  onEditClick,
  onBannerEditClick,
  onEditEmoji,
}) => {
  const { t } = useTranslation()
  const [premiumId] = useAtom(premium.premiumId)
  const { formSubmitStateAtom } = useMemo(
    () =>
      reatomPremiumCurrencySocialLinksForm({
        currencyAddress: currency.address,
        network: currency.network,
        premiumId: premiumId ?? PremiumId(''),
      }),
    [currency.address, currency.network, premiumId],
  )
  const [formSubmitState] = useAtom(formSubmitStateAtom)
  const submitSocials = useAction(formSubmitStateAtom.execute)
  const [socials] = useAtom(premium.socials)
  const ctx = useCtx()

  const form = useForm<LinksFormValues>({
    mode: 'all',
    defaultValues: {
      telegramUrl:
        typeof socials.telegram === 'string' ? socials.telegram : undefined,
      websiteUrl: socials.website,
      xUrl: socials.x ? Socials.getXString(socials.x) : undefined,
    },
  })

  const handleSubmit = form.handleSubmit(async (values) => {
    const result = await submitSocials(values)

    if (Result.isSuccess(result)) {
      const socials = ctx.get(result.success.premiumMeta.socials)
      form.reset({
        telegramUrl:
          typeof socials.telegram === 'string' ? socials.telegram : undefined,
        websiteUrl: socials.website,
        xUrl: socials.x ? Socials.getXString(socials.x) : undefined,
      })
    }
  })

  return (
    <Root>
      <Group>
        <PremiumCurrencyScreenCurrencyImages
          currency={currency}
          pair={pair}
          premium={premium}
          onEditClick={onEditClick}
          onBannerEditClick={onBannerEditClick}
        />
        <PremiumCurrencyDetailsEmoji
          network={currency.network}
          premium={premium}
          onEdit={onEditEmoji}
        />
        <Separator fullWidth />
        <PremiumCurrencyInfoHeading currency={currency} premiumInfo={premium} />
      </Group>
      <PremiumCurrencyInfoAddress currency={currency} mode="secondary" />
      <LinksSection>
        <FormProvider {...form}>
          <Separator fullWidth />
          <LinksForm onSubmit={handleSubmit}>
            <LinksGroup>
              <WebsiteUrlFormControl<LinksFormValues> />
              <TelegramUrlFormControl<LinksFormValues> />
              <XUrlFormControl<LinksFormValues> />
            </LinksGroup>
            <Button
              disabled={!form.formState.isDirty || formSubmitState.loading}
              busy={formSubmitState.loading}
              type="submit"
              radius="rounded"
              color="primary"
            >
              {t('common.save')}
            </Button>
          </LinksForm>
          <Separator fullWidth />
        </FormProvider>
      </LinksSection>
      <PremiumCurrencyDetailsPremium
        currency={currency}
        premiumInfo={premium}
      />
      <PremiumCurrencyMetricsTable pair={pair} />
    </Root>
  )
}
const LinksGroup = styled.div`
  display: grid;
  gap: 6px;
`
const LinksSection = styled.div`
  display: grid;
  gap: 12px;
`
const LinksForm = styled.form`
  display: grid;
  gap: 12px;
`
const Root = styled.div`
  display: grid;
  gap: 16px;
  grid-template-rows: 1fr auto;
  grid-auto-rows: auto;

  ${mediaMinTablet(css`
    gap: 26px;
  `)}
`

const Group = styled.div`
  display: grid;
  align-items: center;
  justify-items: center;
  text-transform: uppercase;
  gap: 16px;
`
